import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  BarChart3,
  MessageSquareText,
  Settings2,
  Copy,
  Check,
  Send,
  ExternalLink,
  Mail,
  MessageSquare,
  Link2,
  ChevronDown,
  ChevronUp,
  Search,
} from "lucide-react";
import { reviewsApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";
import { clientsApi } from "../../services/api";

// ─── Types ──────────────────────────────────────────────────────────────────

interface ReviewStats {
  averageRating: number;
  totalReviews: number;
  googleRedirects: number;
  feedbackReceived: number;
  distribution: { [key: number]: number };
}

interface ReviewEntry {
  _id: string;
  clientId?: string;
  clientName?: string;
  rating: number;
  feedback?: string;
  name?: string;
  email?: string;
  channel?: "email" | "sms" | "link";
  isRead: boolean;
  redirectedToGoogle?: boolean;
  createdAt: string;
}

interface ReviewSettings {
  googleReviewUrl: string;
  autoSendOnClose: boolean;
  autoSendDelay: number; // hours
  customThankYouMessage: string;
  customFeedbackPrompt: string;
}

// ─── Animated Counter ───────────────────────────────────────────────────────

const AnimatedCounter: React.FC<{ value: number; decimals?: number }> = ({
  value,
  decimals = 0,
}) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (value === 0) {
      setDisplay(0);
      return;
    }
    const duration = 600;
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplay(value);
        clearInterval(interval);
      } else {
        setDisplay(current);
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [value]);

  return <>{decimals > 0 ? display.toFixed(decimals) : Math.round(display)}</>;
};

// ─── Star Display ───────────────────────────────────────────────────────────

const StarDisplay: React.FC<{ rating: number; size?: number }> = ({
  rating,
  size = 16,
}) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={size}
        height={size}
      >
        <path
          d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
          fill={star <= rating ? "#F59E0B" : "none"}
          stroke={star <= rating ? "#F59E0B" : "#D1D5DB"}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    ))}
  </div>
);

// ─── Channel Badge ──────────────────────────────────────────────────────────

const ChannelBadge: React.FC<{ channel?: string }> = ({ channel }) => {
  const config: Record<string, { icon: React.FC<any>; label: string; bg: string; text: string }> = {
    email: { icon: Mail, label: "Email", bg: "bg-blue-50", text: "text-blue-600" },
    sms: { icon: MessageSquare, label: "SMS", bg: "bg-purple-50", text: "text-purple-600" },
    link: { icon: Link2, label: "Link", bg: "bg-slate-50", text: "text-slate-600" },
  };
  const c = config[channel || "link"] || config.link;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
};

// ─── Phone Preview Frame ────────────────────────────────────────────────────

const PhonePreview: React.FC<{ settings: ReviewSettings; firmName: string }> = ({
  settings,
  firmName,
}) => (
  <div className="mx-auto w-[260px]">
    <div className="bg-slate-800 rounded-[2rem] p-2 shadow-2xl">
      <div className="bg-slate-800 rounded-t-[1.5rem] pt-2 pb-1 flex justify-center">
        <div className="w-20 h-4 bg-slate-900 rounded-full" />
      </div>
      <div className="bg-white rounded-[1.25rem] overflow-hidden">
        <div className="p-4 text-center">
          <p
            className="font-bold text-sm mb-1"
            style={{ color: "#0F5132" }}
          >
            {firmName}
          </p>
          <p className="text-[10px] text-slate-500 mb-3">
            How was your experience?
          </p>
          <div className="flex justify-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <svg key={s} viewBox="0 0 24 24" className="w-5 h-5">
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  fill={s <= 4 ? "#F59E0B" : "none"}
                  stroke={s <= 4 ? "#F59E0B" : "#D1D5DB"}
                  strokeWidth={1.5}
                />
              </svg>
            ))}
          </div>
          <div className="bg-slate-50 rounded-lg p-2 mb-2">
            <p className="text-[9px] text-slate-400 text-left">
              {settings.customFeedbackPrompt || "We're sorry your experience wasn't perfect..."}
            </p>
          </div>
          <div className="bg-slate-50 rounded-lg p-1.5 mb-2 text-left">
            <p className="text-[8px] text-slate-400">Name (optional)</p>
          </div>
          <div
            className="rounded-lg py-1.5 px-3 text-white text-[9px] font-bold"
            style={{ backgroundColor: "#0F5132" }}
          >
            Submit Feedback
          </div>
        </div>
        <div className="bg-slate-50 py-1.5 text-center">
          <p className="text-[7px] text-slate-400">
            Powered by <span className="font-semibold" style={{ color: "#0F5132" }}>Kavoa</span>
          </p>
        </div>
      </div>
      <div className="bg-slate-800 rounded-b-[1.5rem] py-1 flex justify-center">
        <div className="w-24 h-1 bg-slate-600 rounded-full mt-1" />
      </div>
    </div>
  </div>
);

// ─── Main Component ─────────────────────────────────────────────────────────

const TABS = [
  { key: "overview", label: "Overview", icon: BarChart3 },
  { key: "feedback", label: "Feedback", icon: MessageSquareText },
  { key: "settings", label: "Settings", icon: Settings2 },
] as const;

type TabKey = typeof TABS[number]["key"];

const ReviewsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [stats, setStats] = useState<ReviewStats>({
    averageRating: 0,
    totalReviews: 0,
    googleRedirects: 0,
    feedbackReceived: 0,
    distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  });
  const [reviews, setReviews] = useState<ReviewEntry[]>([]);
  const [reviewLink, setReviewLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState<ReviewSettings>({
    googleReviewUrl: "",
    autoSendOnClose: false,
    autoSendDelay: 48,
    customThankYouMessage: "",
    customFeedbackPrompt: "",
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Feedback tab state
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [filterRead, setFilterRead] = useState<"all" | "read" | "unread">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, reviewsRes, linkRes, settingsRes] = await Promise.all([
        reviewsApi.stats().catch(() => ({ data: null })),
        reviewsApi.list().catch(() => ({ data: { reviews: [] } })),
        reviewsApi.getLink().catch(() => ({ data: { link: "" } })),
        reviewsApi.getSettings().catch(() => ({ data: null })),
      ]);

      if (statsRes.data) {
        const s = statsRes.data?.data || statsRes.data;
        setStats({
          averageRating: s.averageRating || 0,
          totalReviews: s.totalCompleted || s.totalReviews || s.total || 0,
          googleRedirects: s.redirectedToGoogle || s.googleRedirects || s.fiveStarCount || 0,
          feedbackReceived: s.unreadFeedback || s.feedbackReceived || 0,
          distribution: s.ratingDistribution || s.distribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
        });
      }

      const reviewData = reviewsRes.data?.data || reviewsRes.data?.reviews || reviewsRes.data || [];
      setReviews(Array.isArray(reviewData) ? reviewData : []);
      setReviewLink(linkRes.data?.data?.link || linkRes.data?.link || linkRes.data?.url || "");

      if (settingsRes.data) {
        const sd = settingsRes.data?.data || settingsRes.data;
        setSettings((prev) => ({
          ...prev,
          googleReviewUrl: sd.googleReviewUrl || "",
          autoSendOnClose: sd.autoSendAfterMatterClose ?? sd.autoSendOnClose ?? false,
          autoSendDelay: sd.sendDelay ?? sd.autoSendDelay ?? 48,
          customThankYouMessage: sd.customThankYouMessage || "",
          customFeedbackPrompt: sd.customFeedbackPrompt || "",
        }));
      }
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Copy link
  const handleCopyLink = () => {
    if (!reviewLink) return;
    navigator.clipboard.writeText(reviewLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Mark as read
  const handleMarkRead = async (id: string) => {
    try {
      await reviewsApi.markRead(id);
      setReviews((prev) =>
        prev.map((r) => (r._id === id ? { ...r, isRead: true } : r))
      );
    } catch {
      // fail silently
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await reviewsApi.updateSettings(settings);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch {
      // fail silently
    } finally {
      setSettingsSaving(false);
    }
  };

  // Filtered reviews for feedback tab
  const filteredReviews = useMemo(() => {
    return reviews.filter((r) => {
      if (filterRating && r.rating !== filterRating) return false;
      if (filterRead === "read" && !r.isRead) return false;
      if (filterRead === "unread" && r.isRead) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesName = (r.clientName || r.name || "").toLowerCase().includes(q);
        const matchesFeedback = (r.feedback || "").toLowerCase().includes(q);
        if (!matchesName && !matchesFeedback) return false;
      }
      return true;
    });
  }, [reviews, filterRating, filterRead, searchQuery]);

  // Recent feedback for overview
  const recentFeedback = useMemo(() => {
    return reviews
      .filter((r) => r.rating <= 4 && r.feedback)
      .slice(0, 5);
  }, [reviews]);

  // Distribution bar colors
  const barColors: Record<number, string> = {
    5: "bg-emerald-500",
    4: "bg-emerald-400",
    3: "bg-amber-400",
    2: "bg-orange-400",
    1: "bg-red-400",
  };

  const maxDistribution = Math.max(...Object.values(stats.distribution), 1);

  // ─── Render ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div
          className="animate-spin h-8 w-8 rounded-full border-4"
          style={{ borderColor: "#0F5132", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Reviews</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage client feedback and review requests
          </p>
        </div>
        <button
          onClick={() => setSendModalOpen(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold text-sm transition-colors"
          style={{ backgroundColor: "#0F5132" }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
        >
          <Send className="w-4 h-4" />
          Send Review Request
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex-1 justify-center ${
                isActive
                  ? "text-white"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              {isActive && (
                <motion.div
                  layoutId="reviewTab"
                  className="absolute inset-0 rounded-lg"
                  style={{ backgroundColor: "#0F5132" }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <span className="relative flex items-center gap-2">
                <Icon className="w-4 h-4" />
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {/* ─── Overview Tab ─────────────────────────────────────────── */}
        {activeTab === "overview" && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  label: "Average Rating",
                  value: stats.averageRating,
                  decimals: 1,
                  extra: <StarDisplay rating={Math.round(stats.averageRating)} size={14} />,
                  color: "bg-amber-50",
                  iconColor: "text-amber-500",
                  icon: Star,
                },
                {
                  label: "Total Reviews",
                  value: stats.totalReviews,
                  color: "bg-blue-50",
                  iconColor: "text-blue-500",
                  icon: BarChart3,
                },
                {
                  label: "Google Redirects",
                  value: stats.googleRedirects,
                  color: "bg-emerald-50",
                  iconColor: "text-emerald-500",
                  icon: ExternalLink,
                },
                {
                  label: "Feedback Received",
                  value: stats.feedbackReceived,
                  color: "bg-purple-50",
                  iconColor: "text-purple-500",
                  icon: MessageSquareText,
                },
              ].map((card, i) => {
                const CardIcon = card.icon;
                return (
                  <motion.div
                    key={card.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-500">
                        {card.label}
                      </span>
                      <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center`}>
                        <CardIcon className={`w-4 h-4 ${card.iconColor}`} />
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-slate-800">
                      <AnimatedCounter value={card.value} decimals={card.decimals} />
                    </div>
                    {card.extra && <div className="mt-1">{card.extra}</div>}
                  </motion.div>
                );
              })}
            </div>

            {/* Distribution + Share Link */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Rating Distribution */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-4">
                  Rating Distribution
                </h3>
                <div className="space-y-3">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = stats.distribution[star] || 0;
                    const pct =
                      stats.totalReviews > 0
                        ? Math.round((count / stats.totalReviews) * 100)
                        : 0;
                    return (
                      <div key={star} className="flex items-center gap-3">
                        <div className="flex items-center gap-1 w-10 text-sm font-medium text-slate-600">
                          {star}{" "}
                          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-amber-400 fill-amber-400">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        </div>
                        <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full ${barColors[star]} rounded-full`}
                            initial={{ width: 0 }}
                            animate={{
                              width: `${(count / maxDistribution) * 100}%`,
                            }}
                            transition={{ duration: 0.6, delay: (5 - star) * 0.08 }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 w-16 text-right">
                          {count} ({pct}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Share Link */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">
                  Share Review Link
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  Share this link with clients to collect reviews
                </p>

                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 bg-slate-50 rounded-xl px-4 py-3 text-sm text-slate-600 truncate border border-slate-200">
                    {reviewLink || "Loading..."}
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all border border-slate-200 hover:bg-slate-50"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-emerald-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-600">Copy</span>
                      </>
                    )}
                  </button>
                </div>

                <button
                  onClick={() => setSendModalOpen(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-bold text-sm transition-colors"
                  style={{ backgroundColor: "#0F5132" }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#146C43")}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#0F5132")}
                >
                  <Send className="w-4 h-4" />
                  Send to Client
                </button>
              </div>
            </div>

            {/* Recent Feedback */}
            {recentFeedback.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Recent Feedback
                  </h3>
                  <button
                    onClick={() => setActiveTab("feedback")}
                    className="text-xs font-medium hover:underline"
                    style={{ color: "#0F5132" }}
                  >
                    View All
                  </button>
                </div>
                <div className="space-y-3">
                  {recentFeedback.map((r, i) => (
                    <motion.div
                      key={r._id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-50"
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 flex-shrink-0">
                        {(r.clientName || r.name || "?")[0]?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-slate-800">
                            {r.clientName || r.name || "Anonymous"}
                          </span>
                          <StarDisplay rating={r.rating} size={12} />
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {r.feedback}
                        </p>
                      </div>
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ─── Feedback Tab ─────────────────────────────────────────── */}
        {activeTab === "feedback" && (
          <motion.div
            key="feedback"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or feedback..."
                    className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                  />
                </div>

                {/* Rating filter */}
                <div className="flex items-center gap-1">
                  {[null, 5, 4, 3, 2, 1].map((r) => (
                    <button
                      key={r ?? "all"}
                      onClick={() => setFilterRating(r)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        filterRating === r
                          ? "bg-[#0F5132] text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {r === null ? "All" : `${r}★`}
                    </button>
                  ))}
                </div>

                {/* Read/Unread filter */}
                <select
                  value={filterRead}
                  onChange={(e) => setFilterRead(e.target.value as any)}
                  className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
                >
                  <option value="all">All</option>
                  <option value="unread">Unread</option>
                  <option value="read">Read</option>
                </select>
              </div>
            </div>

            {/* Review List */}
            {filteredReviews.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <MessageSquareText className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="text-sm font-semibold text-slate-800 mb-1">
                  No reviews found
                </h3>
                <p className="text-xs text-slate-500">
                  {reviews.length === 0
                    ? "Send your first review request to get started."
                    : "Try adjusting your filters."}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredReviews.map((review, i) => {
                  const isExpanded = expandedId === review._id;
                  return (
                    <motion.div
                      key={review._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-colors ${
                        !review.isRead
                          ? "border-emerald-200 bg-emerald-50/30"
                          : "border-slate-200"
                      }`}
                    >
                      <button
                        onClick={() => {
                          setExpandedId(isExpanded ? null : review._id);
                          if (!review.isRead) handleMarkRead(review._id);
                        }}
                        className="w-full flex items-center gap-4 p-4 text-left"
                      >
                        {/* Unread dot */}
                        <div className="w-2 flex-shrink-0">
                          {!review.isRead && (
                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                          )}
                        </div>

                        {/* Avatar */}
                        <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 flex-shrink-0">
                          {(review.clientName || review.name || "?")[0]?.toUpperCase()}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">
                              {review.clientName || review.name || "Anonymous"}
                            </span>
                            <StarDisplay rating={review.rating} size={12} />
                            <ChannelBadge channel={review.channel} />
                          </div>
                          {review.feedback && (
                            <p className="text-sm text-slate-500 mt-0.5 truncate">
                              {review.feedback}
                            </p>
                          )}
                          {!review.feedback && review.redirectedToGoogle && (
                            <p className="text-sm text-emerald-600 mt-0.5">
                              Redirected to Google Reviews
                            </p>
                          )}
                        </div>

                        {/* Date + chevron */}
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </span>
                        {review.feedback ? (
                          isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          )
                        ) : (
                          <div className="w-4" />
                        )}
                      </button>

                      {/* Expanded content */}
                      <AnimatePresence>
                        {isExpanded && review.feedback && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 pb-4 ml-[3.75rem]">
                              <div className="bg-slate-50 rounded-lg p-4">
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                                  {review.feedback}
                                </p>
                                {(review.name || review.email) && (
                                  <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-4 text-xs text-slate-500">
                                    {review.name && <span>Name: {review.name}</span>}
                                    {review.email && <span>Email: {review.email}</span>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── Settings Tab ─────────────────────────────────────────── */}
        {activeTab === "settings" && (
          <motion.div
            key="settings"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Settings Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* Google Review URL */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h3 className="text-sm font-semibold text-slate-800 mb-1">
                    Google Review URL
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">
                    Paste your Google Business review link. 5-star reviewers will be
                    redirected here.
                  </p>
                  <input
                    type="url"
                    value={settings.googleReviewUrl}
                    onChange={(e) =>
                      setSettings((s) => ({
                        ...s,
                        googleReviewUrl: e.target.value,
                      }))
                    }
                    placeholder="https://g.page/r/your-business/review"
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                  />
                </div>

                {/* Auto-Send */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800">
                        Auto-send review requests
                      </h3>
                      <p className="text-xs text-slate-500">
                        Automatically request reviews after matter closure
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setSettings((s) => ({
                          ...s,
                          autoSendOnClose: !s.autoSendOnClose,
                        }))
                      }
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        settings.autoSendOnClose ? "bg-[#0F5132]" : "bg-slate-300"
                      }`}
                    >
                      <motion.div
                        className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow"
                        animate={{ left: settings.autoSendOnClose ? 22 : 2 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    </button>
                  </div>

                  {settings.autoSendOnClose && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="overflow-hidden"
                    >
                      <label className="block text-xs font-medium text-slate-600 mb-1.5 mt-2">
                        Send review request after closing
                      </label>
                      <select
                        value={settings.autoSendDelay}
                        onChange={(e) =>
                          setSettings((s) => ({
                            ...s,
                            autoSendDelay: Number(e.target.value),
                          }))
                        }
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                      >
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                        <option value={72}>72 hours</option>
                        <option value={168}>1 week</option>
                      </select>
                    </motion.div>
                  )}
                </div>

                {/* Custom Messages */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 mb-1">
                      Custom thank you message
                    </h3>
                    <p className="text-xs text-slate-500 mb-2">
                      Shown to clients who leave a 5-star rating
                    </p>
                    <textarea
                      value={settings.customThankYouMessage}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          customThankYouMessage: e.target.value,
                        }))
                      }
                      placeholder="We're thrilled you had a great experience."
                      rows={3}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 mb-1">
                      Custom feedback prompt
                    </h3>
                    <p className="text-xs text-slate-500 mb-2">
                      Shown to clients who leave 4 stars or fewer
                    </p>
                    <textarea
                      value={settings.customFeedbackPrompt}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          customFeedbackPrompt: e.target.value,
                        }))
                      }
                      placeholder="We're sorry your experience wasn't perfect. Your feedback helps us improve."
                      rows={3}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
                    />
                  </div>
                </div>

                {/* Save */}
                <button
                  onClick={handleSaveSettings}
                  disabled={settingsSaving}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-sm transition-colors disabled:opacity-50"
                  style={{ backgroundColor: "#0F5132" }}
                  onMouseOver={(e) => {
                    if (!settingsSaving) e.currentTarget.style.backgroundColor = "#146C43";
                  }}
                  onMouseOut={(e) => {
                    if (!settingsSaving) e.currentTarget.style.backgroundColor = "#0F5132";
                  }}
                >
                  {settingsSaved ? (
                    <>
                      <Check className="w-4 h-4" />
                      Saved!
                    </>
                  ) : settingsSaving ? (
                    "Saving..."
                  ) : (
                    "Save Settings"
                  )}
                </button>
              </div>

              {/* Preview */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-800">
                  Preview
                </h3>
                <PhonePreview settings={settings} firmName="Your Firm" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Send Review Request Modal */}
      <SendReviewModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        onSent={loadData}
      />
    </div>
  );
};

// ─── Send Review Request Modal ──────────────────────────────────────────────

const SendReviewModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}> = ({ open, onClose, onSent }) => {
  const [mode, setMode] = useState<"client" | "manual">("client");
  const [selectedClient, setSelectedClient] = useState<string>("");
  const [manualName, setManualName] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const [channel, setChannel] = useState<"email" | "sms" | "link">("email");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [clients, setClients] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    if (!open) return;
    clientsApi
      .list({ limit: 500 })
      .then((res) => {
        const list = res.data?.clients || res.data || [];
        setClients(
          list.map((c: any) => ({
            value: c._id,
            label: c.displayName || c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim(),
          }))
        );
      })
      .catch(() => {});
  }, [open]);

  const handleSend = async () => {
    setSending(true);
    try {
      await reviewsApi.send({
        clientId: mode === "client" ? selectedClient : undefined,
        name: mode === "manual" ? manualName : undefined,
        email: mode === "manual" ? manualEmail : undefined,
        phone: mode === "manual" ? manualPhone : undefined,
        channel,
      });
      setSent(true);
      setTimeout(() => {
        onSent();
        onClose();
        // Reset
        setSent(false);
        setSelectedClient("");
        setManualName("");
        setManualEmail("");
        setManualPhone("");
      }, 1500);
    } catch {
      // fail silently
    } finally {
      setSending(false);
    }
  };

  const canSend =
    mode === "client"
      ? !!selectedClient
      : channel === "email"
      ? !!manualEmail
      : channel === "sms"
      ? !!manualPhone
      : !!manualName;

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="Send Review Request"
      subtitle="Request a review from a client"
      icon={Send}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!canSend || sending || sent}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold text-sm transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#0F5132" }}
          >
            {sent ? (
              <>
                <Check className="w-4 h-4" />
                Sent!
              </>
            ) : sending ? (
              "Sending..."
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Request
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Mode toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setMode("client")}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              mode === "client"
                ? "bg-[#0F5132] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Select Client
          </button>
          <button
            onClick={() => setMode("manual")}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              mode === "manual"
                ? "bg-[#0F5132] text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Manual Entry
          </button>
        </div>

        {/* Client select */}
        {mode === "client" && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
            <SearchableSelect
              options={clients}
              value={selectedClient || null}
              onChange={(v) => setSelectedClient(v || "")}
              placeholder="Search for a client..."
            />
          </div>
        )}

        {/* Manual entry */}
        {mode === "manual" && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={manualPhone}
                onChange={(e) => setManualPhone(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
          </div>
        )}

        {/* Channel */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Send via
          </label>
          <div className="flex gap-2">
            {[
              { key: "email" as const, label: "Email", icon: Mail },
              { key: "sms" as const, label: "SMS", icon: MessageSquare },
              { key: "link" as const, label: "Just get link", icon: Link2 },
            ].map((ch) => {
              const Icon = ch.icon;
              return (
                <button
                  key={ch.key}
                  onClick={() => setChannel(ch.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    channel === ch.key
                      ? "bg-[#0F5132] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {ch.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Preview hint */}
        <div className="bg-slate-50 rounded-xl p-4">
          <p className="text-xs text-slate-500">
            {channel === "link"
              ? "A unique review link will be generated. You can share it however you like."
              : `A review request will be sent via ${channel} with a link to your review page.`}
          </p>
        </div>
      </div>
    </PremiumModal>
  );
};

export default ReviewsPage;
